/**
 * Ordering Palworld cards within a set.
 *
 * A card's rarer printings repeat its number with a suffix:
 *
 *     ETD01-012        the base printing
 *     ETD01-012TSR     the SR printing
 *     ETD01-012TSP     the SP printing
 *
 * They should read base -> SR -> SP. Sorting the numbers as plain strings puts
 * SP first, because "TSP" < "TSR" alphabetically, which is the wrong way round.
 *
 * The suffixes in the data are SR, TSR and OSR for the SR tier, and SP, TSP and
 * SSP for the SP tier, so the tier is the last two characters rather than the
 * whole suffix.
 *
 * Soul cards (ESOUL-000 .. ESOUL-008) are not part of any set's own numbering --
 * the same ones are reprinted across sets -- so they go at the end of the list
 * rather than sorting into the middle of it. ("ESOUL" would otherwise land
 * before "ETD01" alphabetically and lead every trial deck.)
 */

const SUFFIX_PATTERN = /^(.*?-\d+)([A-Z]*)$/;

/** Soul cards are shared across sets and are listed after a set's own cards. */
const SOUL_PREFIX = "ESOUL-";

function isSoul(cardNumber: string): boolean {
  return (cardNumber ?? "").toUpperCase().startsWith(SOUL_PREFIX);
}

/** Base printing first, then the SR tier, then the SP tier. */
function suffixRank(suffix: string): number {
  if (!suffix) return 0;
  if (suffix.endsWith("SR")) return 1;
  if (suffix.endsWith("SP")) return 2;
  // Anything new the site introduces sorts after the tiers we know, rather
  // than silently landing in the middle of them.
  return 3;
}

function split(cardNumber: string): { base: string; suffix: string } {
  const match = SUFFIX_PATTERN.exec(cardNumber ?? "");
  return match
    ? { base: match[1], suffix: match[2] }
    : { base: cardNumber ?? "", suffix: "" };
}

/** Compare two Palworld card numbers for display order. */
export function comparePalworldCardNumbers(a: string, b: string): number {
  // Souls last, whatever their number.
  const soulDelta = Number(isSoul(a)) - Number(isSoul(b));
  if (soulDelta !== 0) return soulDelta;

  const left = split(a);
  const right = split(b);

  if (left.base !== right.base) {
    // numeric so -2 sorts before -10.
    return left.base.localeCompare(right.base, "en", { numeric: true });
  }

  const byTier = suffixRank(left.suffix) - suffixRank(right.suffix);
  if (byTier !== 0) return byTier;

  // Same tier (OSR vs SR, say): keep it deterministic.
  return left.suffix.localeCompare(right.suffix, "en");
}
